export interface GoogleAdsAttribution {
  gclid?: string
  gbraid?: string
  wbraid?: string
  capturedAt?: string // ISO datetime
  landingUrl?: string // sanitized first landing URL
}

const STORAGE_KEY = 'tf_lead_form_google_ads_attribution'
const COOKIE_NAME = 'tf_lead_form_ads_attr'
const ROOT_COOKIE_DOMAIN = 'taxforce.ai'
const TTL_DAYS = 90
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function getCookie(name: string): string | undefined {
  if (!isBrowser()) return undefined
  let cookies: string[]
  try {
    cookies = document.cookie ? document.cookie.split('; ') : []
  } catch {
    return undefined
  }
  const prefix = `${encodeURIComponent(name)}=`
  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) {
      return cookie.slice(prefix.length)
    }
  }
  return undefined
}

function shouldUseRootDomainCookie(): boolean {
  if (!isBrowser()) return false
  const hostname = window.location.hostname
  return (
    hostname === ROOT_COOKIE_DOMAIN ||
    hostname.endsWith(`.${ROOT_COOKIE_DOMAIN}`)
  )
}

function setCookie(
  name: string,
  value: string,
  maxAgeSeconds: number,
): boolean {
  if (!isBrowser()) return false

  const parts: string[] = []
  parts.push(`${encodeURIComponent(name)}=${value}`)
  parts.push('Path=/')
  parts.push('SameSite=Lax')
  parts.push(`Max-Age=${maxAgeSeconds}`)

  if (shouldUseRootDomainCookie()) {
    parts.push(`Domain=.${ROOT_COOKIE_DOMAIN}`)
  }

  // Only set Secure on https origins (required by browsers)
  if (window.location.protocol === 'https:') {
    parts.push('Secure')
  }

  try {
    document.cookie = parts.join('; ')
  } catch {
    return false
  }

  // Verify it actually stuck (cookies can be blocked)
  return getCookie(name) === value
}

function deleteCookie(name: string): void {
  if (!isBrowser()) return

  try {
    // Try delete without domain
    document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`

    // Try delete with root domain (if applicable)
    if (shouldUseRootDomainCookie()) {
      document.cookie = `${encodeURIComponent(
        name,
      )}=; Path=/; Max-Age=0; SameSite=Lax; Domain=.${ROOT_COOKIE_DOMAIN}`
    }
  } catch {
    // Cookies can be blocked while localStorage remains available.
  }
}

function readFromLocalStorage(): string | undefined {
  if (!isBrowser()) return undefined
  try {
    return window.localStorage.getItem(STORAGE_KEY) || undefined
  } catch {
    return undefined
  }
}

function writeToLocalStorage(value: string): void {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // ignore
  }
}

function deleteFromLocalStorage(): void {
  if (!isBrowser()) return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

function normalizeAttribution(raw: unknown): GoogleAdsAttribution | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>

  const attribution: GoogleAdsAttribution = {
    gclid: isNonEmptyString(obj.gclid) ? obj.gclid.trim() : undefined,
    gbraid: isNonEmptyString(obj.gbraid) ? obj.gbraid.trim() : undefined,
    wbraid: isNonEmptyString(obj.wbraid) ? obj.wbraid.trim() : undefined,
    capturedAt: isNonEmptyString(obj.capturedAt)
      ? obj.capturedAt.trim()
      : undefined,
    landingUrl: isNonEmptyString(obj.landingUrl)
      ? obj.landingUrl.trim()
      : undefined,
  }

  if (!attribution.gclid && !attribution.gbraid && !attribution.wbraid) {
    return null
  }

  // Validate capturedAt (must be parseable and within TTL)
  if (!attribution.capturedAt) return null
  const capturedAtMs = Date.parse(attribution.capturedAt)
  if (Number.isNaN(capturedAtMs)) return null
  if (Date.now() - capturedAtMs > TTL_MS) return null

  return attribution
}

function readPersistedAttribution(): GoogleAdsAttribution | null {
  if (!isBrowser()) return null

  const cookieValue = getCookie(COOKIE_NAME)
  if (cookieValue) {
    try {
      const decoded = decodeURIComponent(cookieValue)
      const parsed = JSON.parse(decoded)
      const normalized = normalizeAttribution(parsed)
      if (normalized) return normalized
    } catch {
      // fall through
    }
  }

  const localValue = readFromLocalStorage()
  if (localValue) {
    try {
      const parsed = JSON.parse(localValue)
      const normalized = normalizeAttribution(parsed)
      if (normalized) return normalized
    } catch {
      // fall through
    }
  }

  return null
}

function buildSanitizedLandingUrl(url: URL): string {
  const allowedParams = [
    // Google click IDs
    'gclid',
    'gbraid',
    'wbraid',
    // Common campaign params (safe allowlist; helps debugging attribution)
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ]

  const allowed = new URLSearchParams()
  for (const key of allowedParams) {
    const value = url.searchParams.get(key)
    if (value) allowed.set(key, value)
  }

  const qs = allowed.toString()
  return qs
    ? `${url.origin}${url.pathname}?${qs}`
    : `${url.origin}${url.pathname}`
}

function parseAttributionFromUrl(): GoogleAdsAttribution | null {
  if (!isBrowser()) return null

  const url = new URL(window.location.href)
  const gclid = url.searchParams.get('gclid') || undefined
  const gbraid = url.searchParams.get('gbraid') || undefined
  const wbraid = url.searchParams.get('wbraid') || undefined

  const hasAny =
    isNonEmptyString(gclid) ||
    isNonEmptyString(gbraid) ||
    isNonEmptyString(wbraid)
  if (!hasAny) return null

  return {
    gclid: isNonEmptyString(gclid) ? gclid.trim() : undefined,
    gbraid: isNonEmptyString(gbraid) ? gbraid.trim() : undefined,
    wbraid: isNonEmptyString(wbraid) ? wbraid.trim() : undefined,
    capturedAt: new Date().toISOString(),
    landingUrl: buildSanitizedLandingUrl(url),
  }
}

function persistAttribution(attribution: GoogleAdsAttribution): void {
  if (!isBrowser()) return

  const json = JSON.stringify(attribution)
  const encoded = encodeURIComponent(json)

  const maxAgeSeconds = Math.floor(TTL_MS / 1000)
  const cookieOk = setCookie(COOKIE_NAME, encoded, maxAgeSeconds)

  // Always also try localStorage as a fallback (some environments block cookies)
  if (!cookieOk) {
    writeToLocalStorage(json)
  } else {
    writeToLocalStorage(json)
  }
}

function clearPersistedAttribution(): void {
  deleteCookie(COOKIE_NAME)
  deleteFromLocalStorage()
}

/**
 * Get Google Ads click identifiers (gclid + gbraid/wbraid) for this lead session.
 *
 * Default behavior is **first-touch**:
 * - If we already persisted an attribution object within TTL, we return it and DO NOT overwrite.
 * - Otherwise, if the current landing URL has click ids, we persist them and return them.
 */
export function getGoogleAdsAttribution(): GoogleAdsAttribution | null {
  if (!isBrowser()) return null

  const persisted = readPersistedAttribution()
  if (persisted) return persisted

  // Clean up any stale/broken stored value so we can recapture.
  clearPersistedAttribution()

  const fromUrl = parseAttributionFromUrl()
  if (!fromUrl) return null

  persistAttribution(fromUrl)
  return fromUrl
}

/**
 * @internal Test helper
 */
export function __clearGoogleAdsAttributionForTests(): void {
  clearPersistedAttribution()
}
